import { useEffect, useState, type ReactNode } from 'react';
import {
  Button,
  Checkbox,
  Field,
  Input,
  Dropdown,
  Option,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import {
  emptySiteAvailability,
  getOrderSiteAvailability,
  saveOrderSiteAvailability,
  deleteOrderSiteAvailability,
  siteAvailabilityRequests,
  getApiErrorMessage,
  type SiteAvailabilityDraft,
  type SiteAvailabilityField,
} from '@moko/utils';
import FormSection from './Layout/FormSection';
import { TimezoneSelect } from './TimezoneSelect';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { TimeSelect } from './TimeSelect';
import { formatDate } from '@moko/utils';
import ContentSkeleton from './ContentSkeleton/ContentSkeleton';

export const siteAvailabilityFieldId = (
  windowIndex: number,
  field: SiteAvailabilityField
) =>
  field === 'window'
    ? `site-availability-window-${windowIndex}`
    : `site-availability-${windowIndex}-${field}`;

const formatAvailabilityTime = (time: string) => {
  const match = time.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return time || '—';

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return time;

  return `${hours % 12 || 12}:${String(minutes).padStart(2, '0')}${
    hours < 12 ? 'am' : 'pm'
  }`;
};

const useStyles = makeStyles({
  summaryWindow: {
    marginBottom: tokens.spacingVerticalXXL,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    rowGap: tokens.spacingVerticalM,
    columnGap: tokens.spacingHorizontalXXL,
    marginTop: tokens.spacingVerticalS,
    marginBottom: '0',
    '@media (min-width: 600px)': {
      gridTemplateColumns: '1fr 1fr',
    },
    '@media (min-width: 900px)': {
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr)) minmax(0, 1.5fr)',
    },
  },
  summaryLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    marginBottom: tokens.spacingVerticalXXS,
  },
  summaryValue: {
    color: tokens.colorNeutralForeground1,
    wordBreak: 'break-word',
    marginLeft: '0',
  },
  sectionTitle: {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    paddingBottom: tokens.spacingVerticalS,
    marginTop: '0',
    marginBottom: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
});

type Props = {
  orderId?: number;
  value?: SiteAvailabilityDraft[];
  onChange?: (rows: SiteAvailabilityDraft[]) => void;
  readOnly?: boolean;
  source?: string;
  timezoneId?: string;
  timezonePending?: boolean;
  onLoadedTimezone?: (timezoneId: string) => void;
  layout?: 'stacked' | 'split';
  children?: ReactNode;
  fieldError?: {
    windowIndex: number;
    field: SiteAvailabilityField;
    message: string;
  } | null;
};
export const OrderSiteAvailability = ({
  orderId,
  value = [],
  onChange,
  readOnly = false,
  source = 'Client',
  timezoneId,
  timezonePending = false,
  onLoadedTimezone,
  layout = 'stacked',
  children,
  fieldError,
}: Props) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const [stored, setStored] = useState<SiteAvailabilityDraft[]>([]);
  const [dirty, setDirty] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [retry, setRetry] = useState(0);
  const [loadFailed, setLoadFailed] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const rows = orderId ? stored : value;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  useEffect(() => {
    if (!orderId) return;
    const controller = new AbortController();
    setStored([]);
    setDirty(new Set());
    setLoading(true);
    setError('');
    setMessage('');
    setLoadFailed(false);
    getOrderSiteAvailability(controller, orderId)
      .then((rows) => {
        if (!controller.signal.aborted) setStored(rows);
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          setError(
            getApiErrorMessage(err, 'Unable to load site availability.')
          );
          setLoadFailed(true);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [orderId, retry]);
  useEffect(() => {
    if (loading || !onLoadedTimezone || rows.length === 0) return;
    const ids = [...new Set(rows.map((row) => row.timezoneId).filter(Boolean))];
    if (ids.length === 1) onLoadedTimezone(ids[0]);
  }, [loading, rows, onLoadedTimezone]);
  useEffect(() => {
    if (!timezoneId) return;
    const current = orderId ? stored : value;
    if (current.every((row) => row.timezoneId === timezoneId)) return;
    const next = current.map((row) =>
      row.timezoneId === timezoneId ? row : { ...row, timezoneId }
    );
    if (orderId) setStored(next);
    else onChange?.(next);
    if (!orderId) return;
    setDirty((dirty) => {
      const marked = new Set(dirty);
      current.forEach((row, index) => {
        if (row.timezoneId !== timezoneId) marked.add(index);
      });
      return marked;
    });
  }, [timezoneId, orderId, stored, value, onChange]);
  const change = (next: SiteAvailabilityDraft[]) => {
    setMessage('');
    if (orderId) setStored(next);
    else onChange?.(next);
  };
  const patch = (index: number, patch: Partial<SiteAvailabilityDraft>) => {
    change(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    setDirty((current) => new Set(current).add(index));
  };
  const save = async (index: number) => {
    if (!orderId) return;
    setError('');
    setBusy(true);
    try {
      siteAvailabilityRequests(rows);
      const id = await saveOrderSiteAvailability(
        new AbortController(),
        orderId,
        rows[index]
      );
      setStored((current) =>
        current.map((row, i) => (i === index ? { ...row, id } : row))
      );
      setDirty((current) => {
        const next = new Set(current);
        next.delete(index);
        return next;
      });
      setMessage(t('siteAvailabilitySaved', 'Site availability saved.'));
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to save site availability.'));
    } finally {
      setBusy(false);
    }
  };
  const clone = (index: number) => {
    const copy = { ...rows[index], id: undefined };
    change([...rows.slice(0, index + 1), copy, ...rows.slice(index + 1)]);
    setDirty(
      (current) =>
        new Set(
          [...current].map((i) => (i > index ? i + 1 : i)).concat(index + 1)
        )
    );
    setConfirmDelete((current) =>
      current != null && current > index ? current + 1 : current
    );
  };
  const remove = async (index: number) => {
    setError('');
    setBusy(true);
    try {
      if (rows[index].id)
        await deleteOrderSiteAvailability(
          new AbortController(),
          rows[index].id!
        );
      change(rows.filter((_, i) => i !== index));
      setDirty(
        (current) =>
          new Set(
            [...current]
              .filter((i) => i !== index)
              .map((i) => (i > index ? i - 1 : i))
          )
      );
      setConfirmDelete(null);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to remove site availability.'));
    } finally {
      setBusy(false);
    }
  };
  const fieldMessage = (index: number, field: SiteAvailabilityField) =>
    fieldError?.windowIndex === index && fieldError.field === field
      ? fieldError.message
      : undefined;
  const help = (
    <>
      <p>
        {t(
          'siteAvailabilityHelp',
          'Optional dates and times when the site is available. These are possible times, not a confirmed appointment.'
        )}
      </p>
      {timezoneId ? (
        <p>
          {t(
            'allTimesInTimezone',
            'All the times are in {{timezone}} timezone',
            { timezone: timezoneId }
          )}
        </p>
      ) : null}
      {orderId && !readOnly && (
        <p>
          {t(
            'siteAvailabilityOrderHelp',
            'Save each window separately. Changes here do not update existing Work appointments or availability.'
          )}
        </p>
      )}
    </>
  );
  const windows = (
    <>
      {loading && <ContentSkeleton variant="lines" />}
      {error && (
        <div role="alert">
          {error}
          {loadFailed && (
            <Button type="button" onClick={() => setRetry((x) => x + 1)}>
              Retry
            </Button>
          )}
        </div>
      )}
      {message && <div role="status">{message}</div>}
      {!loading && !loadFailed && rows.length === 0 && (
        <p>
          {t(
            'siteAvailabilityUnknown',
            'No times supplied — availability needs confirmation.'
          )}
        </p>
      )}
      {rows.map((row, index) =>
        readOnly ? (
          <div
            key={row.id ?? `summary-${index}`}
            className={styles.summaryWindow}
          >
            <Text weight="semibold">
              {t('availabilityWindowNumber', 'Window {{number}}', {
                number: index + 1,
              })}
            </Text>
            <dl className={styles.summaryGrid}>
              {[
                [
                  t('date', 'Date'),
                  row.date ? formatDate(new Date(`${row.date}T00:00:00`)) : '—',
                ],
                [
                  t('startTime', 'Start time'),
                  formatAvailabilityTime(row.startTime),
                ],
                [t('endTime', 'End time'), formatAvailabilityTime(row.endTime)],
                ...(timezoneId
                  ? []
                  : [[t('timezone', 'Timezone'), row.timezoneId || '—']]),
                [
                  t('durationMinutes', 'Duration (minutes)'),
                  String(row.duration),
                ],
                [
                  t('priority', 'Priority'),
                  row.priority === 'Preferred'
                    ? t('preferred', 'Preferred')
                    : row.priority === 'SecondPreference'
                    ? t('secondPreference', 'Second preference')
                    : t('noPreference', 'No preference'),
                ],
                [
                  t('status', 'Status'),
                  row.status
                    ? t('active', 'Active')
                    : t('inactive', 'Inactive'),
                ],
              ].map(([caption, value]) => (
                <div key={caption}>
                  <dt className={styles.summaryLabel}>{caption}</dt>
                  <dd className={styles.summaryValue}>{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : (
          <fieldset
            id={siteAvailabilityFieldId(index, 'window')}
            key={row.id ?? `new-${index}`}
            disabled={readOnly || busy || loading}
            style={{
              marginBottom: 12,
              padding: 12,
              border: `1px solid ${
                fieldMessage(index, 'window')
                  ? tokens.colorPaletteRedBorder2
                  : '#ccc'
              }`,
              borderRadius: 6,
            }}
          >
            <legend>
              {t('availabilityWindowNumber', 'Window {{number}}', {
                number: index + 1,
              })}
              {dirty.has(index) ? ' — Unsaved' : ''}
            </legend>
            {fieldMessage(index, 'window') ? (
              <Text
                style={{
                  color: tokens.colorPaletteRedForeground1,
                  display: 'block',
                  marginBottom: 8,
                }}
              >
                {fieldMessage(index, 'window')}
              </Text>
            ) : null}
            <div
              style={{
                display: 'grid',
                gap: 12,
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                alignItems: 'start',
              }}
            >
              <Field
                id={siteAvailabilityFieldId(index, 'date')}
                label={t('date', 'Date')}
                required
                validationState={fieldMessage(index, 'date') ? 'error' : 'none'}
                validationMessage={fieldMessage(index, 'date')}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  minDate={today}
                  value={
                    row.date ? new Date(`${row.date}T00:00:00`) : undefined
                  }
                  formatDate={(date) => (date ? formatDate(date) : '')}
                  disabled={readOnly || busy || loading}
                  onSelectDate={(date) => {
                    const pad = (value: number) =>
                      String(value).padStart(2, '0');
                    patch(index, {
                      date: date
                        ? `${date.getFullYear()}-${pad(
                            date.getMonth() + 1
                          )}-${pad(date.getDate())}`
                        : '',
                    });
                  }}
                />
              </Field>
              <Field
                id={siteAvailabilityFieldId(index, 'startTime')}
                label={t('startTime', 'Start time')}
                required
                validationState={
                  fieldMessage(index, 'startTime') ? 'error' : 'none'
                }
                validationMessage={fieldMessage(index, 'startTime')}
              >
                <TimeSelect
                  value={row.startTime}
                  disabled={readOnly || busy || loading}
                  onChange={(value) =>
                    patch(index, { startTime: value.slice(0, 5) })
                  }
                />
              </Field>
              <Field
                id={siteAvailabilityFieldId(index, 'endTime')}
                label={t('endTime', 'End time')}
                required
                validationState={
                  fieldMessage(index, 'endTime') ? 'error' : 'none'
                }
                validationMessage={fieldMessage(index, 'endTime')}
              >
                <TimeSelect
                  value={row.endTime}
                  disabled={readOnly || busy || loading}
                  onChange={(value) =>
                    patch(index, { endTime: value.slice(0, 5) })
                  }
                />
              </Field>
              {!timezoneId && !timezonePending ? (
                <Field
                  id={siteAvailabilityFieldId(index, 'timezoneId')}
                  label={t('timezone', 'Timezone')}
                  required
                  validationState={
                    fieldMessage(index, 'timezoneId') ? 'error' : 'none'
                  }
                  validationMessage={fieldMessage(index, 'timezoneId')}
                >
                  <TimezoneSelect
                    disabled={readOnly || busy}
                    value={row.timezoneId}
                    onChange={(nextTimezoneId) =>
                      patch(index, { timezoneId: nextTimezoneId })
                    }
                  />
                </Field>
              ) : null}
              <Field
                id={siteAvailabilityFieldId(index, 'duration')}
                label={t('durationMinutes', 'Duration (minutes)')}
                validationState={
                  fieldMessage(index, 'duration') ? 'error' : 'none'
                }
                validationMessage={fieldMessage(index, 'duration')}
              >
                <Input
                  type="number"
                  min={15}
                  max={720}
                  step={15}
                  value={String(row.duration)}
                  onChange={(_, d) =>
                    patch(index, { duration: Number(d.value) })
                  }
                />
              </Field>
              <Field label={t('priority', 'Priority')}>
                <Dropdown
                  style={{ minWidth: 0, width: '100%' }}
                  disabled={readOnly || busy || loading}
                  selectedOptions={[row.priority]}
                  value={
                    row.priority === 'Preferred'
                      ? t('preferred', 'Preferred')
                      : row.priority === 'SecondPreference'
                      ? t('secondPreference', 'Second preference')
                      : t('noPreference', 'No preference')
                  }
                  onOptionSelect={(_, data) => {
                    if (
                      data.optionValue === 'None' ||
                      data.optionValue === 'Preferred' ||
                      data.optionValue === 'SecondPreference'
                    )
                      patch(index, { priority: data.optionValue });
                  }}
                >
                  <Option value="None">
                    {t('noPreference', 'No preference')}
                  </Option>
                  <Option value="Preferred">
                    {t('preferred', 'Preferred')}
                  </Option>
                  <Option value="SecondPreference">
                    {t('secondPreference', 'Second preference')}
                  </Option>
                </Dropdown>
              </Field>
              {(orderId || !row.status) && (
                <Field label={t('status', 'Status')}>
                  <Checkbox
                    style={{ minHeight: 32, alignSelf: 'flex-start' }}
                    disabled={readOnly || busy || loading}
                    label={t('active', 'Active')}
                    checked={row.status}
                    onChange={(_, d) =>
                      patch(index, { status: d.checked === true })
                    }
                  />
                </Field>
              )}
            </div>
            {!readOnly && (
              <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                {orderId && (
                  <Button
                    type="button"
                    appearance="primary"
                    disabled={busy || (!!row.id && !dirty.has(index))}
                    onClick={() => void save(index)}
                  >
                    {t('saveWindow', 'Save window')}
                  </Button>
                )}
                <Button
                  type="button"
                  disabled={busy}
                  onClick={() => clone(index)}
                >
                  {t('clone', 'Clone')}
                </Button>
                <Button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    row.id ? setConfirmDelete(index) : void remove(index)
                  }
                >
                  {t('remove', 'Remove')}
                </Button>
                {confirmDelete === index && (
                  <>
                    <Text>Remove this availability window?</Text>
                    <Button type="button" onClick={() => void remove(index)}>
                      Confirm remove
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setConfirmDelete(null)}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </div>
            )}
          </fieldset>
        )
      )}
      {!readOnly && (
        <Button
          type="button"
          disabled={busy || loading || loadFailed}
          onClick={() => {
            change([
              ...rows,
              {
                ...emptySiteAvailability(source),
                ...(timezoneId ? { timezoneId } : {}),
              },
            ]);
            setDirty((current) => new Set(current).add(rows.length));
          }}
        >
          {t('addAvailabilityWindow', 'Add availability window')}
        </Button>
      )}
    </>
  );
  if (layout === 'split') {
    return (
      <FormSection
        title={t('siteAvailability', 'Site availability')}
        description={help}
      >
        {children}
        {windows}
      </FormSection>
    );
  }
  return (
    <section
      aria-label={t('siteAvailability', 'Site availability')}
      style={{ marginTop: 20, marginBottom: 20 }}
    >
      <h3 className={styles.sectionTitle}>
        {t('siteAvailability', 'Site availability')}
      </h3>
      {children}
      {help}
      {windows}
    </section>
  );
};
